/**
 * The one colour rule for case-status badges: pending → green, disposed → red,
 * anything else → blue.
 *
 * The Find Cases wizard used to pick its own palette per screen — results tables
 * showed Disposed in green while the review page showed the same case with an
 * orange "Pending" pill, so the colour told you nothing. Import from here
 * instead of hand-writing the classes.
 *
 * Two variants, same rule:
 *   SOFT  — tinted pill for status columns inside results tables.
 *   SOLID — filled pill for the badge beside the case title on review screens.
 *
 * Callers that already know the tone from something other than the status text
 * (e.g. High Court infers "disposed" from a decision date) can index the maps
 * directly with a tone key.
 */

export const STATUS_PILL_SOFT = {
  pending: "bg-green-50 text-green-600",
  disposed: "bg-red-50 text-red-600",
  other: "bg-blue-50 text-blue-600",
};

export const STATUS_PILL_SOLID = {
  pending: "bg-green-500",
  disposed: "bg-red-500",
  other: "bg-blue-500",
};

/** Shared layout for a table status pill; pair with a STATUS_PILL_SOFT colour. */
export const STATUS_PILL_BASE =
  "inline-flex rounded-md px-2 py-1 text-xs font-semibold";

/**
 * Classify a raw status string. Court portals phrase the same two states many
 * ways ("Pending", "Case is pending", "Disposed off", "Decided", "Closed"), so
 * match on substrings rather than equality.
 *
 * Order matters. An explicit "pending" always wins; the disposal words are
 * checked before the live-matter synonyms so "Disposed after hearing" reads as
 * disposed rather than as a hearing still to come.
 */
export function statusTone(status) {
  const s = String(status || "").toLowerCase();
  if (!s) return "other";
  if (s.includes("pending")) return "pending";
  if (s.includes("dispos") || s.includes("decided") || s.includes("closed")) {
    return "disposed";
  }
  // Stage-of-case wording that means the matter is still live: District Court
  // reports "Hearing"/"Appearance", High Court reports "Admission".
  if (s.includes("hearing") || s.includes("appearance") || s.includes("admission")) {
    return "pending";
  }
  return "other";
}

export const statusPillSoft = (status) => STATUS_PILL_SOFT[statusTone(status)];

export const statusPillSolid = (status) => STATUS_PILL_SOLID[statusTone(status)];
